import { useState, useEffect, useCallback } from 'react';
import {
  MCPTask,
  TaskFilters,
  UseTasksState,
  TaskCreateRequest,
  TaskUpdateRequest,
  TaskExecution,
} from '../types';
import { apiService, APIError } from '../services/api';

export const useTasks = (initialFilters: TaskFilters = {}) => {
  const [state, setState] = useState<UseTasksState>({
    tasks: [],
    loading: false,
    error: null,
    selectedTask: null,
  });

  const [filters, setFilters] = useState<TaskFilters>(initialFilters);

  const loadTasks = useCallback(
    async (currentFilters = filters) => {
      console.log('🔄 useTasks: loadTasks iniciando...', currentFilters);
      setState(prev => ({ ...prev, loading: true, error: null }));

      try {
        const tasks = await apiService.getTasks(currentFilters);
        console.log(
          '✅ useTasks: Tasks cargadas exitosamente:',
          tasks.length,
          'tasks'
        );
        console.log('📋 useTasks: Primera task de ejemplo:', tasks[0]);
        setState(prev => ({
          ...prev,
          tasks,
          loading: false,
        }));
      } catch (error) {
        console.error('❌ useTasks: Error cargando tasks:', error);
        const apiError =
          error instanceof APIError
            ? error
            : new APIError('Error al cargar tareas');
        setState(prev => ({
          ...prev,
          loading: false,
          error: apiError,
        }));
      }
    },
    [filters]
  );

  const createTask = useCallback(async (taskData: TaskCreateRequest) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const newTask = await apiService.createTask(taskData);
      setState(prev => ({
        ...prev,
        tasks: [...prev.tasks, newTask],
        loading: false,
      }));
      return newTask;
    } catch (error) {
      const apiError =
        error instanceof APIError
          ? error
          : new APIError('Error al crear tarea');
      setState(prev => ({
        ...prev,
        loading: false,
        error: apiError,
      }));
      return null;
    }
  }, []);

  const updateTask = useCallback(async (taskData: TaskUpdateRequest) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const updatedTask = await apiService.updateTask(taskData);
      setState(prev => ({
        ...prev,
        tasks: prev.tasks.map(task =>
          task.id === updatedTask.id ? updatedTask : task
        ),
        selectedTask:
          prev.selectedTask?.id === updatedTask.id
            ? updatedTask
            : prev.selectedTask,
        loading: false,
      }));
      return updatedTask;
    } catch (error) {
      const apiError =
        error instanceof APIError
          ? error
          : new APIError('Error al actualizar tarea');
      setState(prev => ({
        ...prev,
        loading: false,
        error: apiError,
      }));
      return null;
    }
  }, []);

  const deleteTask = useCallback(async (id: string) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      await apiService.deleteTask(id);
      setState(prev => ({
        ...prev,
        tasks: prev.tasks.filter(task => task.id !== id),
        selectedTask: prev.selectedTask?.id === id ? null : prev.selectedTask,
        loading: false,
      }));
      return true;
    } catch (error) {
      const apiError =
        error instanceof APIError
          ? error
          : new APIError('Error al eliminar tarea');
      setState(prev => ({
        ...prev,
        loading: false,
        error: apiError,
      }));
      return false;
    }
  }, []);

  const executeTask = useCallback(
    async (id: string, input: string): Promise<TaskExecution | null> => {
      setState(prev => ({ ...prev, loading: true, error: null }));

      try {
        const execution = await apiService.executeTask(id, input);

        // Actualizar estadísticas de la tarea
        setState(prev => ({
          ...prev,
          tasks: prev.tasks.map(task => {
            if (task.id === id) {
              return {
                ...task,
                executions: task.executions + 1,
                lastRun: execution.timestamp,
                successRate: execution.success
                  ? (task.successRate * task.executions + 100) /
                    (task.executions + 1)
                  : (task.successRate * task.executions) /
                    (task.executions + 1),
              };
            }
            return task;
          }),
          loading: false,
        }));

        return execution;
      } catch (error) {
        const apiError =
          error instanceof APIError
            ? error
            : new APIError('Error al ejecutar tarea');
        setState(prev => ({
          ...prev,
          loading: false,
          error: apiError,
        }));
        return null;
      }
    },
    []
  );

  const selectTask = useCallback((task: MCPTask | null) => {
    setState(prev => ({ ...prev, selectedTask: task }));
  }, []);

  const updateFilters = useCallback(
    (newFilters: TaskFilters) => {
      setFilters(newFilters);
      loadTasks(newFilters);
    },
    [loadTasks]
  );

  const refreshTasks = useCallback(() => {
    loadTasks();
  }, [loadTasks]);

  const getTasksByCategory = useCallback(
    (category: string) => {
      if (category === 'Todos') {
        return state.tasks;
      }
      return state.tasks.filter(task => task.category === category);
    },
    [state.tasks]
  );

  const getTasksByStatus = useCallback(
    (status: string) => {
      return state.tasks.filter(task => task.status === status);
    },
    [state.tasks]
  );

  // Cargar datos iniciales
  useEffect(() => {
    loadTasks();
  }, []);

  return {
    ...state,
    filters,
    updateFilters,
    createTask,
    updateTask,
    deleteTask,
    executeTask,
    selectTask,
    refreshTasks,
    getTasksByCategory,
    getTasksByStatus,
  };
};
